import React, { useRef, useEffect, useState } from "react";
import { Eraser } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PrivacyNotice } from "@/components/PrivacyNotice";

export function SignaturePad({ value, onChange, testId, signerName, noticeText }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const [hasInk, setHasInk] = useState(!!value);
  const [signedAt, setSignedAt] = useState("");

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    ctx.lineWidth = 2.2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#111827";
    if (value && typeof value === "string" && value.startsWith("data:image")) {
      const img = new Image();
      img.onload = () => ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      img.src = value;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pos = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const t = e.touches ? e.touches[0] : e;
    return {
      x: (t.clientX - rect.left) * (canvasRef.current.width / rect.width),
      y: (t.clientY - rect.top) * (canvasRef.current.height / rect.height),
    };
  };
  const start = (e) => {
    drawing.current = true;
    const ctx = canvasRef.current.getContext("2d");
    const p = pos(e);
    ctx.beginPath();
    ctx.moveTo(p.x, p.y);
  };
  const move = (e) => {
    if (!drawing.current) return;
    if (e.cancelable) e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const p = pos(e);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    setHasInk(true);
  };
  const end = () => {
    if (!drawing.current) return;
    drawing.current = false;
    setSignedAt(new Date().toLocaleString("en-GB"));
    onChange(canvasRef.current.toDataURL("image/png"));
  };
  const clear = () => {
    const c = canvasRef.current;
    c.getContext("2d").clearRect(0, 0, c.width, c.height);
    setHasInk(false);
    setSignedAt("");
    onChange("");
  };

  return (
    <div>
      <canvas
        ref={canvasRef}
        width={600}
        height={160}
        className="w-full touch-none rounded-lg border border-slate-300 bg-white"
        onMouseDown={start}
        onMouseMove={move}
        onMouseUp={end}
        onMouseLeave={end}
        onTouchStart={start}
        onTouchMove={move}
        onTouchEnd={end}
        data-testid={testId}
      />
      <div className="mt-1 flex items-center justify-between">
        <span className="text-xs text-slate-400" data-testid="signature-status">
          {hasInk
            ? `${signerName ? `Signed by ${signerName}` : "Signed"}${signedAt ? ` · ${signedAt}` : ""}`
            : "Sign above with mouse or finger"}
        </span>
        <Button type="button" size="sm" variant="ghost" onClick={clear} data-testid="signature-clear">
          <Eraser className="mr-1 h-3.5 w-3.5" /> Clear
        </Button>
      </div>
      <PrivacyNotice className="mt-1.5" testId="esign-notice">
        {noticeText ||
          "By signing you confirm these details are accurate. Your name and the date & time are recorded with this signature as an audit record."}
      </PrivacyNotice>
    </div>
  );
}

export default SignaturePad;
